var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { array, number } from "yup";
import { axiosPrivate } from "../../../api/axios";
import { NONE_INGREDIENT_ID } from "../../../OrderFlow/hooks/RecipeSelection/recipeSelectionUtils";
import { LogLevel } from "../../hooks/useLogging/Logging-types";
import { InputHelperText, InputTypes, OPTION_PREFIX, } from "./CustomizeModal-types";
import { Tokens } from "../../sharedTypes/Tokens";
/**
 * Func to consistently name React Hook Form customize option form value.
 * @param i ItemIngredient.
 * @returns Stringed customize option field name. E.g., "option-id", where "id" is the ingredient option id.
 */
export const getCustomizationFieldName = (i) => `${OPTION_PREFIX}${i.IngredientId}`;
export const getExistingCustomizationFieldName = (id) => id ? `${OPTION_PREFIX}${id}` : undefined;
/**
 * Creates yup object schema for all ingredients, to be spread into the statically defined schema.
 * @param questions
 * @returns yup object schema
 */
export const buildCustomizationInputSchema = (ingredientInputMap) => {
    if (!ingredientInputMap || (ingredientInputMap === null || ingredientInputMap === void 0 ? void 0 : ingredientInputMap.length) === 0)
        return {};
    return Object.fromEntries(ingredientInputMap.map((i) => [getCustomizationFieldName(i.ingredient), i.yupSchema]));
};
/**
 * Set applicable options as the form default's value or pass an empty string.
 *
 * @param ingredients
 * @param recipe
 * @param assignment
 * @param conflicts
 */
export const getDefaultCustomizeOptions = (ingredients, recipe, assignment, conflicts) => {
    var _a;
    // Initialize the return values
    const optionsMap = {};
    const substitutionNames = [];
    if ((assignment === null || assignment === void 0 ? void 0 : assignment.SelectedIngredientOptions) && ((_a = assignment === null || assignment === void 0 ? void 0 : assignment.SelectedIngredientOptions) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        // Populate from assignment's selected ingredient options
        assignment.SelectedIngredientOptions.forEach((ing) => {
            const fieldName = getExistingCustomizationFieldName(ing.originalIngredientId);
            if (!fieldName)
                return;
            const selectedOptions = ing.selectedOptions.map((ingOption) => { var _a; return (_a = ingOption.replacementIngredientOptionId) !== null && _a !== void 0 ? _a : NONE_INGREDIENT_ID; });
            optionsMap[fieldName] = selectedOptions;
            // Collect names for substitution text, ignoring NONE_INGREDIENT_ID
            ing.selectedOptions
                .filter((opt) => opt.replacementIngredientId !== NONE_INGREDIENT_ID && opt.replacementIngredientName)
                .forEach((opt) => substitutionNames.push(opt.replacementIngredientName));
        });
    }
    else if ((recipe === null || recipe === void 0 ? void 0 : recipe.RecipeIngredientActions.length) && (ingredients === null || ingredients === void 0 ? void 0 : ingredients.length)) {
        // Populate from recipe actions and ingredient details if assignment isn't provided
        ingredients.forEach((ing) => {
            var _a, _b, _c;
            const fieldName = getCustomizationFieldName(ing);
            const replacementAction = recipe.RecipeIngredientActions.find((r) => r.OriginalIngredientId === ing.IngredientId);
            // Determine the appropriate sub ingredient ID and name to use
            const subIngredientId = (_b = (_a = replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.IngredientOptionId) !== null && _a !== void 0 ? _a : replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientId1) !== null && _b !== void 0 ? _b : replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientId2;
            const subIngredientName = (_c = replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientName1) !== null && _c !== void 0 ? _c : replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientName2;
            const multipleValues = ing.IngredientOptionMaximum > 1;
            const isOptional = ing.IngredientOptionMinimum === 0 && ing.IngredientOptionMaximum === 1;
            const noneOption = ing.IngredientOptions.find((opt) => opt.IngredientId === NONE_INGREDIENT_ID);
            if (replacementAction &&
                !multipleValues &&
                ((replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientId1) || (replacementAction === null || replacementAction === void 0 ? void 0 : replacementAction.SubIngredientId2))) {
                optionsMap[fieldName] = subIngredientId;
                if (subIngredientName && subIngredientId !== NONE_INGREDIENT_ID) {
                    substitutionNames.push(subIngredientName);
                }
            }
            else if (multipleValues) {
                const defaultSelectedOption = subIngredientId ? [subIngredientId] : [];
                const nonConflictingOptions = ing.IngredientOptions.filter((opt) => {
                    if (opt.IngredientOptionId === subIngredientId || opt.IngredientId === subIngredientId)
                        return false;
                    if (!opt.IngredientMajorRelations || opt.IngredientMajorRelations.length === 0)
                        return true;
                    return !!opt.IngredientMajorRelations.find((imr) => !(conflicts === null || conflicts === void 0 ? void 0 : conflicts.includes(imr)));
                });
                const remainder = Math.max(ing.IngredientOptionMinimum - defaultSelectedOption.length, 0);
                const selectedIngredients = nonConflictingOptions
                    .slice(0, remainder)
                    .map((option) => option.IngredientOptionId);
                optionsMap[fieldName] = [...defaultSelectedOption, ...selectedIngredients].filter((id) => id !== undefined);
                nonConflictingOptions.slice(0, remainder).forEach((opt) => {
                    if (opt.IngredientId !== NONE_INGREDIENT_ID && opt.IngredientName) {
                        substitutionNames.push(opt.IngredientName);
                    }
                });
            }
            else if (isOptional) {
                optionsMap[fieldName] = noneOption === null || noneOption === void 0 ? void 0 : noneOption.IngredientOptionId;
            }
            else {
                optionsMap[fieldName] = ing.IngredientOptions[0].IngredientOptionId;
                if (ing.IngredientOptions[0].IngredientOptionId !== NONE_INGREDIENT_ID) {
                    substitutionNames.push(ing.IngredientOptions[0].IngredientName);
                }
            }
        });
    }
    else if (ingredients === null || ingredients === void 0 ? void 0 : ingredients.length) {
        // Populate defaults if no assignment or recipe details exist
        ingredients.forEach((ing) => {
            var _a, _b, _c, _d;
            if (ing.IngredientOptions.length > 0) {
                const fieldName = getCustomizationFieldName(ing);
                const defaultOption = (_c = (_b = (_a = ing.IngredientOptions) === null || _a === void 0 ? void 0 : _a.find((opt) => opt.IsDefaultOption)) === null || _b === void 0 ? void 0 : _b.IngredientOptionId) !== null && _c !== void 0 ? _c : NONE_INGREDIENT_ID;
                optionsMap[fieldName] = defaultOption;
                // Collect substitution names if it's a selected default, ensuring not to include NONE
                if (defaultOption !== NONE_INGREDIENT_ID) {
                    substitutionNames.push((_d = ing.IngredientOptions.find((opt) => opt.IsDefaultOption)) === null || _d === void 0 ? void 0 : _d.IngredientName);
                }
            }
        });
    }
    // Construct the substitution text from names collected
    const substitutionText = substitutionNames.length === 1
        ? substitutionNames[0]
        : substitutionNames.length > 1
            ? `${substitutionNames.slice(0, -1).join(", ")} and ${substitutionNames.slice(-1)}`
            : "";
    // Filter out undefined values before returning
    return {
        options: Object.keys(optionsMap).length > 0
            ? Object.fromEntries(Object.entries(optionsMap).filter(([, value]) => value !== undefined) // Remove undefined values
            )
            : undefined,
        substitutionText,
    };
};
/**
 * Calculates the total price of the selected customize options
 *
 * @param ingredients - MustReplace ingredient(s)
 * @param formValues - Selected options from the form
 * @returns
 */
export const getTotalReplacementCost = (ingredients, formValues) => {
    let replacementCost = 0;
    if (!ingredients || !formValues)
        return replacementCost;
    Object.entries(formValues).map(([name, value]) => {
        const originalIngredientId = name.replace(OPTION_PREFIX, "");
        const selectedIngredientId = value;
        const ingredientToReplace = ingredients.find((ing) => { var _a; return ((_a = ing.IngredientId) === null || _a === void 0 ? void 0 : _a.toString()) === originalIngredientId; });
        if (Array.isArray(selectedIngredientId)) {
            selectedIngredientId.forEach((ing) => {
                const selectedCustomizeOption = ingredientToReplace === null || ingredientToReplace === void 0 ? void 0 : ingredientToReplace.IngredientOptions.find((opt) => { var _a; return ((_a = opt.IngredientOptionId) === null || _a === void 0 ? void 0 : _a.toString()) === (ing === null || ing === void 0 ? void 0 : ing.toString()); });
                replacementCost += selectedCustomizeOption ? selectedCustomizeOption === null || selectedCustomizeOption === void 0 ? void 0 : selectedCustomizeOption.IngredientPrice : 0;
            });
        }
        else {
            const selectedCustomizeOption = ingredientToReplace === null || ingredientToReplace === void 0 ? void 0 : ingredientToReplace.IngredientOptions.find((opt) => { var _a; return ((_a = opt.IngredientOptionId) === null || _a === void 0 ? void 0 : _a.toString()) === (selectedIngredientId === null || selectedIngredientId === void 0 ? void 0 : selectedIngredientId.toString()); });
            replacementCost += selectedCustomizeOption ? selectedCustomizeOption === null || selectedCustomizeOption === void 0 ? void 0 : selectedCustomizeOption.IngredientPrice : 0;
        }
    });
    return replacementCost;
};
/**
 * Builds Object of active customizations for all recipe assignments
 *
 * @param assignments - Meal Assignments from Order Flow or My Meals
 * @param baseIngredients - Array of ingredients for a recipe
 * @returns
 */
export const getSelectedIngredientOptionsForAssignments = (assignments, baseIngredients) => {
    if (!assignments || !baseIngredients)
        return;
    let selectedCustomizeOptions = [];
    assignments.forEach((assignment) => {
        if (assignment.SelectedIngredientOptions) {
            assignment.SelectedIngredientOptions.forEach((so) => {
                const ingredientToReplace = baseIngredients.find((ing) => ing.MustReplace && ing.IngredientId === so.originalIngredientId);
                if (ingredientToReplace) {
                    so.selectedOptions.forEach((selectedOption) => {
                        const selectedCustomizeOption = ingredientToReplace.IngredientOptions.find((opt) => opt.IngredientOptionId === selectedOption.replacementIngredientOptionId);
                        if (selectedCustomizeOption) {
                            const existingEntry = selectedCustomizeOptions.find((entry) => entry.baseIngredient.IngredientId === ingredientToReplace.IngredientId);
                            if (existingEntry) {
                                existingEntry.selectedIngredientOptions.push(selectedCustomizeOption);
                            }
                            else {
                                selectedCustomizeOptions.push({
                                    baseIngredient: ingredientToReplace,
                                    selectedIngredientOptions: [selectedCustomizeOption],
                                });
                            }
                        }
                    });
                }
            });
        }
    });
    return selectedCustomizeOptions;
};
/**
 * Builds a map of ingredient inputs based on the provided ingredient's minimum, maximum and ingredient options.
 *
 * @param {ItemIngredient[]} [ingredients] - The array of ingredient items.
 * @returns {IngredientInputMapType[]} The map of ingredient inputs.
 */
export const buildIngredientInputMap = (ingredients, customizationsOptionsSubtext) => {
    const ingredientInputMap = [];
    ingredients === null || ingredients === void 0 ? void 0 : ingredients.forEach((ing) => {
        const { IngredientOptionMinimum: min, IngredientOptionMaximum: max, IngredientName: name, IngredientOptions: options, } = ing;
        const isCheckbox = min === 0 && max > 1;
        const isOptional = min === 0 && max === 1;
        const isRequired = min >= 1 && max >= 1;
        let helperText = replacePlaceholders(InputHelperText.DEFAULT, min, max, customizationsOptionsSubtext, true);
        if (isCheckbox) {
            ingredientInputMap.push({
                inputType: InputTypes.CHECKBOX,
                ingredient: ing,
                required: false,
                minLimit: min,
                maxLimit: max,
                multiple: true,
                yupSchema: createYupSchema(InputTypes.CHECKBOX, false, max > 1, min, max, customizationsOptionsSubtext),
                helperText: replacePlaceholders(InputHelperText.UP_TO_MAX, min, max, customizationsOptionsSubtext, false),
            });
        }
        else if (isOptional) {
            const inputType = options.length > 3 ? InputTypes.DROPDOWN : InputTypes.RADIOBUTTON;
            ingredientInputMap.push({
                inputType,
                ingredient: ing,
                required: false,
                minLimit: min,
                maxLimit: max,
                multiple: false,
                yupSchema: createYupSchema(inputType, false, max > 1, min, max),
                helperText: replacePlaceholders(InputHelperText.UP_TO_MAX, min, max, customizationsOptionsSubtext, false),
            });
        }
        else if (isRequired) {
            if (min === max) {
                helperText = replacePlaceholders(InputHelperText.MUST_SELECT, min, max, customizationsOptionsSubtext, false);
                if (max === 1) {
                    helperText = replacePlaceholders(InputHelperText.UP_TO_MAX, min, max, customizationsOptionsSubtext, false);
                }
            }
            const inputType = max > 1 || options.length > 3 ? InputTypes.DROPDOWN : InputTypes.RADIOBUTTON;
            ingredientInputMap.push({
                inputType,
                ingredient: ing,
                required: true,
                minLimit: min,
                maxLimit: max,
                multiple: max > 1,
                yupSchema: createYupSchema(inputType, true, max > 1, min, max),
                helperText,
            });
        }
    });
    return ingredientInputMap;
};
/**
 * Creates a Yup schema based on the type of input, whether it's required, whether it allows multiple selections,
 * and the minimum and maximum limits for the number of selections.
 *
 * @param {string} type - The type of input ('checkbox', 'dropdown' or 'radiobutton').
 * @param {boolean} required - Whether the input is required.
 * @param {boolean} multiple - Whether the input allows multiple selections.
 * @param {number} min - The minimum number of selections allowed.
 * @param {number} max - The maximum number of selections allowed.
 * @returns {any} The Yup schema for the input.
 */
const createYupSchema = (type, required, multiple, min, max, customizationsOptionsSubtext) => {
    const mustSelectText = replacePlaceholders(InputHelperText.MUST_SELECT, min, max, customizationsOptionsSubtext, false);
    const exceededMaxText = replacePlaceholders(InputHelperText.EXCEEDED_MAX, min, max, customizationsOptionsSubtext, true);
    if (type === InputTypes.CHECKBOX || (type === InputTypes.DROPDOWN && multiple)) {
        if (required) {
            return array().min(min, mustSelectText).max(max, mustSelectText).required(exceededMaxText);
        }
        return array().min(min, mustSelectText).max(max, exceededMaxText).notRequired().nullable();
    }
    return number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .nullable();
};
const removeParentheses = (text) => text.replace(/\((.*?)\)/g, "$1");
const replacePlaceholders = (text, min, max, customizationsOptionsSubtext, shouldRemoveParentheses = false) => {
    // Use custom subtext if available, otherwise default to "ingredient(s)"
    let customizedSubtext = customizationsOptionsSubtext || "ingredient(s)";
    // Remove parentheses from customizedSubtext if shouldRemoveParentheses is true
    if (shouldRemoveParentheses) {
        customizedSubtext = removeParentheses(customizedSubtext);
    }
    return text
        .replace(Tokens.Min, min.toString())
        .replace(Tokens.Max, max.toString())
        .replace(Tokens.Ingredients, customizedSubtext);
};
/**
 * Processes selected customization options and updates the assignment.
 *
 * @param {Object} selectedCustomizeOptions - An object where keys are option names and values are selected ingredient IDs or arrays of IDs.
 * @param {Array} ingredientsToReplace - An array of ingredients with replacement options.
 * @param {Object} assignment - The assignment object that will be updated with selected ingredient options.
 */
export const processSelectedCustomizeOptions = (selectedCustomizeOptions, ingredientsToReplace, assignment) => {
    const processOption = (selectedOption, originalIngredient, originalIngredientId) => {
        var _a, _b, _c, _d;
        // Create a new option object with the selected ingredient details
        const newOption = {
            replacementIngredientId: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.IngredientId,
            replacementIngredientOptionId: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.IngredientOptionId,
            replacementIngredientName: (_a = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.Name) !== null && _a !== void 0 ? _a : selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.IngredientName,
            replacementPrice: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.IngredientPrice,
            Unit: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.Unit,
            ReplacementQty: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.Quantity,
        };
        // Find if there is an existing option in the assignment that matches the original ingredient
        const existingOption = (_b = assignment === null || assignment === void 0 ? void 0 : assignment.SelectedIngredientOptions) === null || _b === void 0 ? void 0 : _b.find((i) => i.originalIngredientId === (originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.IngredientId));
        // If an existing option is found, push the new option to its selectedOptions array
        if (existingOption) {
            existingOption.selectedOptions.push(newOption);
        }
        else {
            // Otherwise, create a new entry in targetAssignment with the new option
            (_c = assignment === null || assignment === void 0 ? void 0 : assignment.SelectedIngredientOptions) === null || _c === void 0 ? void 0 : _c.push({
                originalIngredientName: originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.IngredientName,
                mustBeReplaced: (_d = originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.MustReplace) !== null && _d !== void 0 ? _d : false,
                originalIngredientId: Number(originalIngredientId),
                recipeIngredientId: originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.RecipeIngredientId,
                selectedOptions: [newOption],
            });
        }
    };
    if (!selectedCustomizeOptions)
        return undefined;
    Object.entries(selectedCustomizeOptions).forEach(([name, value]) => {
        const originalIngredientId = name.replace(OPTION_PREFIX, "");
        const selectedIngredients = Array.isArray(value) ? value : [value];
        const originalIngredient = ingredientsToReplace === null || ingredientsToReplace === void 0 ? void 0 : ingredientsToReplace.find((i) => { var _a; return ((_a = i.IngredientId) === null || _a === void 0 ? void 0 : _a.toString()) === originalIngredientId; });
        const ingredientOptions = originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.IngredientOptions;
        selectedIngredients.forEach((option) => {
            const selectedOption = ingredientOptions === null || ingredientOptions === void 0 ? void 0 : ingredientOptions.find((s) => s.IngredientOptionId === option);
            if (selectedOption) {
                processOption(selectedOption, originalIngredient, originalIngredientId);
            }
        });
    });
};
/**
 * Checks if a recipe is customizable based on the existence of at least one MustReplace ingredient.
 * An ingredient is considered a MustReplace if it has ingredient options.
 *
 * @param {Recipe["RecipeIngredients"]} [ingredients] - The list of ingredients in the recipe.
 * @returns {boolean} `true` if there are any ingredients that must be replaced; otherwise, `false`.
 */
export const isRecipeCustomizable = (ingredients) => {
    if (!ingredients || (ingredients === null || ingredients === void 0 ? void 0 : ingredients.length) === 0)
        return false;
    return ingredients.some((ingredient) => ingredient.MustReplace);
};
/**
 * Determines if there are any conflicts between ingredient options and omissions.
 *
 * @param {IngredientOption[]} ingredientOptions - The list of ingredient options.
 * @param {number[]} [omissions] - An array of omission IDs to check against.
 * @returns {boolean} `true` if there are any conflicts between ingredient options and omissions; otherwise, `false`.
 */
export const hasIngredientOptionConflicts = (ingredientOptions, omissions) => {
    if (!omissions || (omissions === null || omissions === void 0 ? void 0 : omissions.length) === 0)
        return false;
    return ingredientOptions === null || ingredientOptions === void 0 ? void 0 : ingredientOptions.some((io) => io.IngredientMajorRelations.some((a) => omissions.includes(a)));
};
/**
 * Fetches nutrition data for a recipe with customizations and sets state for the customization modal
 *
 * @param {number | undefined} recipeId - The ID of the recipe for which to fetch nutrition data.
 * @param {IngredientReplacement[]} ingredientReplacements - Array of ingredient replacements to consider when calculating nutrition.
 * @param {React.Dispatch<React.SetStateAction<Nutrition | undefined>>} setNutritionForSelections - Function to update the state with the fetched nutrition data or undefined in case of an error.
 * @param {DataDogLog} datadogLog - Function to log errors or important information for monitoring.
 */
export const getNutritionForCustomizations = (recipeId, ingredientReplacements, setNutritionForSelections, datadogLog) => __awaiter(void 0, void 0, void 0, function* () {
    const data = yield getRecipeNutrition(recipeId, ingredientReplacements, datadogLog);
    setNutritionForSelections(data);
});
/**
 * Builds the request body for ingredient replacements from form values.
 *
 * @param {Record<string, any>} values - Form values containing ingredient replacement options. Each key represents an ingredient ID prefixed with "option-", and the value can be an array or a single replacement ID.
 *
 * @returns {IngredientReplacement[]} - An array of ingredient replacements formatted for API submission.
 */
export const buildNutritionRequestBody = (values, recipe) => {
    let body = [];
    Object.entries(values).forEach(([name, value]) => {
        const originalIngredientId = name.replace(OPTION_PREFIX, "");
        const selectedIngredients = Array.isArray(value) ? value : [value];
        selectedIngredients.forEach((option) => {
            var _a, _b;
            const recipeIngredient = recipe.RecipeIngredients.find((ri) => ri.IngredientOptions &&
                ri.IngredientOptions.length > 0 &&
                ri.IngredientOptions.some((io) => io.IngredientOptionId == option));
            const ingredientOption = (_a = recipeIngredient === null || recipeIngredient === void 0 ? void 0 : recipeIngredient.IngredientOptions) === null || _a === void 0 ? void 0 : _a.find((io) => io.IngredientOptionId == option);
            body.push({
                originalIngredientId: +originalIngredientId,
                replacementIngredientId: ingredientOption.IngredientId,
                replacementUnit: ingredientOption.Unit,
                replacementQuantity: (_b = ingredientOption.Quantity) !== null && _b !== void 0 ? _b : 0,
            });
        });
    });
    return body;
};
/**
 * Fetches nutrition data for a recipe with customizations.
 *
 * @param {number | undefined} recipeId - The ID of the recipe for which to fetch nutrition data.
 * @param {IngredientReplacement[]} ingredientReplacements - Array of ingredient replacements to consider when calculating nutrition.
 * @param {DataDogLog} datadogLog - Function to log errors or important information for monitoring.
 */
export const getRecipeNutrition = (recipeId, ingredientReplacements, datadogLog) => __awaiter(void 0, void 0, void 0, function* () {
    return yield axiosPrivate
        .post(`/React/GetRecipeNutrition?recipeId=${recipeId}`, ingredientReplacements)
        .then((response) => {
        const { data } = response;
        if (!data.success) {
            datadogLog(`getRecipeNutrition failed. Error: ${data.message}`, LogLevel.ERROR);
            return undefined;
        }
        return data.result;
    })
        .catch((err) => {
        datadogLog("Caught exception in getRecipeNutrition", LogLevel.ERROR, undefined, err);
        return undefined;
    });
});
